import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, Loader2, Utensils } from "lucide-react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";

const fmtISK = (n) => `${Math.round(n || 0).toLocaleString("en-US").replace(/,/g, ".")} ISK`;
const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }); }
  catch { return d; }
};

const BUCKETS = [
  { key: "paid", label: "Paid", color: "text-emerald-400" },
  { key: "refunded", label: "Refunded / Cancelled", color: "text-[#C44536]" },
  { key: "comp", label: "Free (Comp)", color: "text-amber-300" },
  { key: "unpaid", label: "Not paid", color: "text-gray-400" },
];

const Th = ({ children, right }) => (
  <th className={`px-4 py-3 text-[10px] uppercase tracking-[0.14em] text-gray-500 font-bold ${right ? "text-right" : "text-left"}`}>{children}</th>
);
const Td = ({ children, right, className = "" }) => (
  <td className={`px-4 py-3 ${right ? "text-right tabular-nums" : ""} ${className}`}>{children}</td>
);

const SummaryRow = ({ label, t, color, testid, bold }) => (
  <tr data-testid={testid} className={`border-t border-white/5 ${bold ? "bg-white/[0.04] font-bold" : ""}`}>
    <Td className={color}>{label}</Td>
    <Td right>{t.bookings}</Td>
    <Td right>{t.party_size}</Td>
    <Td right>{t.fire}</Td>
    <Td right>{t.ocean}</Td>
    <Td right>{t.earth}</Td>
    <Td right>{t.pairings}</Td>
    <Td right>{fmtISK(t.charged)}</Td>
  </tr>
);

export default function DiningSalesReport() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/dining-sales-report").then((r) => setData(r.data)).catch(() => toast.error("Could not load report"));
  }, []);

  const exportCsv = () => {
    fetch(`${API}/admin/dining-sales-report/export.csv`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("immersion_admin_token")}` },
    })
      .then((r) => r.blob())
      .then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = "fine-dining-sales.csv";
        a.click();
      })
      .catch(() => toast.error("Export failed"));
  };

  if (!data) {
    return <div className="min-h-screen bg-black text-gray-500 flex items-center justify-center gap-2"><Loader2 className="animate-spin" size={18} /> Loading…</div>;
  }


  return (
    <div data-testid="dining-sales-page" className="min-h-screen bg-black text-white">
      <div className="border-b border-white/10 px-6 md:px-12 py-5 flex items-center gap-4">
        <Link to="/admin" data-testid="dining-back-to-admin" className="text-gray-400 hover:text-white inline-flex items-center gap-2 text-sm"><ArrowLeft size={16} /> Dashboard</Link>
        <span className="font-display font-bold tracking-tight text-lg ml-2 inline-flex items-center gap-2"><Utensils size={18} className="text-amber-300" /> Fine Dining Sales</span>
        <button data-testid="dining-export-csv" onClick={exportCsv} className="ml-auto border border-white/20 hover:border-white/60 uppercase tracking-[0.15em] text-xs font-bold py-2.5 px-5 transition-colors inline-flex items-center gap-2"><Download size={14} /> CSV</button>
      </div>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 space-y-12">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {BUCKETS.map((b) => (
            <div key={b.key} data-testid={`dining-card-${b.key}`} className="border border-white/10 bg-[#0A0A0C] p-5">
              <div className="text-[11px] uppercase tracking-[0.15em] text-gray-500">{b.label}</div>
              <div className={`font-display font-extrabold text-3xl mt-2 ${b.color}`}>{data.buckets[b.key].bookings}</div>
              <div className="text-xs text-gray-400 mt-1">{data.buckets[b.key].party_size} guests · {fmtISK(data.buckets[b.key].charged)}</div>
            </div>
          ))}
          <div data-testid="dining-card-total" className="border border-white/30 bg-[#0A0A0C] p-5 col-span-2 lg:col-span-1">
            <div className="text-[11px] uppercase tracking-[0.15em] text-gray-500">Total ordered</div>
            <div className="font-display font-extrabold text-3xl mt-2">{data.total.bookings}</div>
            <div className="text-xs text-gray-400 mt-1">{data.total.party_size} guests</div>
          </div>
        </div>

        <section>
          <h2 className="font-display font-bold uppercase tracking-tight text-lg mb-4">Breakdown by status</h2>
          <div className="border border-white/10 bg-[#0A0A0C] overflow-x-auto">
            <table className="w-full text-sm" data-testid="dining-summary-table">
              <thead><tr><Th>Status</Th><Th right>Bookings</Th><Th right>Guests</Th><Th right>Fire</Th><Th right>Ocean</Th><Th right>Earth</Th><Th right>Pairings</Th><Th right>Amount</Th></tr></thead>
              <tbody>
                {BUCKETS.map((b) => <SummaryRow key={b.key} label={b.label} t={data.buckets[b.key]} color={b.color} testid={`dining-summary-${b.key}`} />)}
                <SummaryRow label="Total ordered" t={data.total} bold testid="dining-summary-total" />
                {data.purged.length > 0 && <SummaryRow label="Purged unpaid carts" t={data.purged_total} color="text-gray-500" testid="dining-summary-purged" />}
                {data.purged.length > 0 && (
                  <SummaryRow label="Grand total incl. purged" bold testid="dining-summary-grand"
                    t={Object.fromEntries(Object.keys(data.total).map((k) => [k, data.total[k] + data.purged_total[k]]))} />
                )}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Paid revenue {fmtISK(data.buckets.paid.charged)} = {fmtISK(data.buckets.paid.value)} dinners &amp; pairings + {fmtISK(data.buckets.paid.card_fee)} card fees.
            Refunded amount shows what was charged before the refund. Purged carts are only tracked from purges made after this report was added.
          </p>
        </section>

        <section>
          <h2 className="font-display font-bold uppercase tracking-tight text-lg mb-4">By night &amp; seating</h2>
          <div className="border border-white/10 bg-[#0A0A0C] overflow-x-auto">
            <table className="w-full text-sm" data-testid="dining-night-table">
              <thead><tr><Th>Night</Th><Th>Seating</Th><Th right>Paid</Th><Th right>Refunded</Th><Th right>Free</Th><Th right>Unpaid</Th><Th right>Total guests</Th><Th right>Fire / Ocean / Earth</Th><Th right>Pairings</Th><Th right>Paid revenue</Th></tr></thead>
              <tbody>
                {data.by_night.length === 0 && <tr><td colSpan={10} className="px-4 py-10 text-center text-gray-600">No dinner bookings yet.</td></tr>}
                {data.by_night.map((n) => (
                  <tr key={`${n.date}-${n.seating}`} data-testid={`dining-night-${n.date}-${n.seating}`} className="border-t border-white/5">
                    <Td>{fmtDate(n.date)}</Td><Td>{n.seating}</Td>
                    <Td right className="text-emerald-400">{n.paid.party_size}</Td>
                    <Td right className="text-[#e07a6c]">{n.refunded.party_size}</Td>
                    <Td right className="text-amber-300">{n.comp.party_size}</Td>
                    <Td right className="text-gray-400">{n.unpaid.party_size}</Td>
                    <Td right className="font-bold">{n.all.party_size}</Td>
                    <Td right>{n.all.fire} / {n.all.ocean} / {n.all.earth}</Td>
                    <Td right>{n.all.pairings}</Td>
                    <Td right>{fmtISK(n.paid.charged)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

      </div>
    </div>
  );
}
